import { supabase } from '@/integrations/supabase/client';

export interface QualificationDocument {
  filename: string;
  file_path: string;
  file_type: string;
  file_size?: number;
  uploaded_at?: string;
}

export const parseQualificationDocuments = (documentUrl: string | null): QualificationDocument[] => {
  if (!documentUrl) return [];
  
  try {
    // Try to parse as JSON array first (new format)
    const parsed = JSON.parse(documentUrl);
    if (Array.isArray(parsed)) {
      return parsed.map(doc => ({
        filename: doc.filename || 'Unknown Document',
        file_path: doc.file_path || doc,
        file_type: doc.file_type || 'application/pdf',
        file_size: doc.file_size,
        uploaded_at: doc.uploaded_at
      }));
    }
  } catch (error) {
    // Fallback for single URL (backward compatibility)
    return [{
      filename: 'Qualification Document',
      file_path: documentUrl,
      file_type: 'application/pdf'
    }];
  }
  
  return [];
};

export const generateSignedUrl = async (filePath: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase.storage
      .from('qualification-documents')
      .createSignedUrl(filePath, 3600); // 1 hour expiry
    
    if (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }
    
    return data.signedUrl;
  } catch (error) {
    console.error('Exception generating signed URL:', error);
    return null;
  }
};

export const downloadDocument = async (doc: QualificationDocument): Promise<void> => {
  try {
    const signedUrl = await generateSignedUrl(doc.file_path);
    
    if (!signedUrl) {
      throw new Error('Failed to generate download URL');
    }
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = signedUrl;
    link.download = doc.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

export const getFileTypeIcon = (fileType: string): string => {
  if (fileType.includes('pdf')) return '📄';
  if (fileType.includes('image')) return '🖼️';
  return '📁';
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
};